import type {
  Actions,
  AddressDetails,
  ICheckoutContext,
  SubscriptionCheckoutState,
  SuccessScreenContext,
} from '@/types/context';
import type {
  AvailablePaymentMethod,
  CheckoutConfigurations,
  CheckoutData,
  Customer,
  Offer,
  PaymentMethod,
  Pricing,
} from '@/types/checkout';
import type { BootData, CheckoutTheme } from '@/types/boot';
import { TemplateType } from '@/types/boot';
import type { SubscriptionsSummary } from '@/types/subscription';
import { PaymentMethodEnum, ProviderEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import type { PayFromExpressCheckout } from '@/types/payment';
import { SolutionModel, SolutionModelSubtype } from '@/constants/checkoutSolutionModel';
import { getTemplateConfig } from '@/constants/templateConfig';
import { CHARGEBEE_PAYMENT_METHOD_TYPE_BY_PSP_NAME } from '@/constants/chargebee';
import { formatTotalPrice } from '@/utils/pricing';
import { initialPromoCodeState } from '@/utils/promoCode';
import { sanitizeMerchantRedirectUrl } from '@/utils/merchantRedirectUrl';
import { organizeBoxPaymentMethods } from '@/utils/paymentMethods';
import type { FeatureFlag } from '@/constants/featureFlags';
import type { CardContext, DrawerContext, ViewerContext } from '@/types/context';

/**
 * Adapts the subscription summary into the one-time-payment `ICheckoutContext`
 * so the existing UI (PayButton, InputTaxComponent, Email, WalletButtonShell,
 * Payment column, SuccessScreen, ...) renders with ZERO edits — the same trick
 * v2's SubscriptionContextBridge used.
 *
 * Every one-time-payment field a subscription has no analogue for is set
 * inert HERE, in one documented place, instead of scattering
 * `if (isSubscription)` through components:
 *  - `offers`, `savedCards`, `promoCodeBreakdown`, saved-card / PayPal-vault state — empty
 *  - `startOrderPolling` — no-op (the controller drives subscription polling
 *    itself after finalize)
 *  - redirect-tab / Apple-Pay-tab / 3DS state machinery — no-op (Chargebee
 *    owns 3DS internally via `authorizeWith3ds`)
 *  - `createSession` — rejects (Nuvei-only concept; Chargebee is never Nuvei)
 */

/**
 * The three methods Chargebee Fields-mode + wallet handlers serve, listed
 * unconditionally — no device probing, exactly like the one-time-payment flow,
 * where the methods come off the wire as-is and each wallet reports its own
 * support once it loads. `ApplePaySession.canMakePayments()` in particular
 * answers `false` inside cross-origin iframes and in-app browsers (a QR-code
 * landing is usually one), so consulting it would hide Apple Pay from shoppers
 * who can pay with it. Chargebee's drop-in decides instead: it can't mount,
 * `ChargebeeApplePayForm` goes `unavailable` and the box collapses.
 *
 * Everything else comes from `summary.paymentMethods` — the APMs the gateway
 * routed for the plan (BE order kept, appended after the three). The gateway
 * deliberately leaves these three out of its list, so a repeat is dropped
 * rather than shown twice.
 */
export const buildSubscriptionPaymentMethods = (
  summary?: Pick<SubscriptionsSummary, 'paymentMethods'>
): PaymentMethod[] => {
  const base = { pspSubMidPk: undefined, publicConfig: undefined, submidName: '', provider: ProviderEnum.CHARGEBEE };
  const fixed: PaymentMethod[] = [
    {
      ...base,
      id: 'chargebee-card',
      paymentMethodName: PaymentMethodEnum.CREDIT_CARD,
      paymentMethodDisplayName: 'Credit card',
      pspPaymentMethodName: 'card',
    },
    {
      ...base,
      id: 'chargebee-google-pay',
      paymentMethodName: PaymentMethodEnum.GOOGLE_PAY,
      paymentMethodDisplayName: 'Google Pay',
      pspPaymentMethodName: 'google_pay',
    },
    {
      ...base,
      id: 'chargebee-apple-pay',
      paymentMethodName: PaymentMethodEnum.APPLE_PAY,
      paymentMethodDisplayName: 'Apple Pay',
      pspPaymentMethodName: 'apple_pay',
    },
  ];
  return [...fixed, ...buildSummaryApms(summary?.paymentMethods ?? [], fixed)];
};

/**
 * The route has exactly one PSP integration — Chargebee, with Stripe as ITS
 * gateway — so a routed record's `provider` is forced to CHARGEBEE: a `stripe`
 * record would otherwise send `GenericPayForm` / `dispatchByProvider` down the
 * Stripe branch, which has no Stripe instance or client secret on this route.
 * `pspPaymentMethodName` is likewise Stripe's string on those rows; it is
 * renamed to Chargebee's `payment_method_type` where the two differ
 * (`kr_card` → `south_korean_cards`), since the Payment Component is keyed on
 * it. Other routing fields (`id`, `publicConfig`) pass through.
 */
const buildSummaryApms = (routed: PaymentMethod[], fixed: PaymentMethod[]): PaymentMethod[] => {
  const seen = new Set<string>(fixed.map((method) => method.paymentMethodName));
  seen.add(PaymentMethodEnum.OTHER);
  return routed.flatMap((method) => {
    if (seen.has(method.paymentMethodName)) return [];
    seen.add(method.paymentMethodName);
    return [{ ...method, provider: ProviderEnum.CHARGEBEE, pspPaymentMethodName: toChargebeeType(method) }];
  });
};

const toChargebeeType = (method: PaymentMethod): string | null => {
  const pspName = method.pspPaymentMethodName ?? method.paymentMethodName;
  return CHARGEBEE_PAYMENT_METHOD_TYPE_BY_PSP_NAME[pspName] ?? method.pspPaymentMethodName;
};

/**
 * Every method is a selectable BOX (never the express row): Chargebee's Apple
 * Pay is a drop-in panel rather than a bare button, so a wallet row can't hold
 * it — and with the boxes, card / Google Pay / Apple Pay are picked the same
 * way v2 lets a shopper pick them. Past four the rest overflow into the same
 * "Other" tile the one-time picker uses.
 */
const buildAvailablePaymentMethods = (methods: PaymentMethod[]): AvailablePaymentMethod[] =>
  organizeBoxPaymentMethods(methods);

const buildSubscriptionOffer = (summary: SubscriptionsSummary): Offer => ({
  name: summary.plan.displayName || summary.plan.name,
  displayName: summary.plan.displayName,
  description: summary.plan.description,
  sku: summary.plan.providerPlanId,
  assetUrl: '',
  id: summary.plan._id,
});

const buildCheckoutConfigurations = (boot: BootData): CheckoutConfigurations => ({
  showSaveMyCard: false,
  showLinkSavedCard: false,
  solutionModel: SolutionModel.INDEPENDENT_CHECKOUT,
  solutionModelSubtype: SolutionModelSubtype.WEB_CHECKOUT,
  shouldShowPayPalAsApm: false,
  shouldShowGooglePayAsApm: false,
  featureFlags: boot.featureFlags,
  footerConfiguration: { footerText: '', defaultCAText: '', defaultText: '', fullFooterConfig: {} },
});

/**
 * Shaped like `/checkout/v3`'s CheckoutData purely for the SDK event builders
 * (`getEventDataForSdkEvents` / `notifyPaymentIntentFailed`), which type on it.
 */
export const buildSubscriptionCheckoutData = (
  summary: SubscriptionsSummary,
  boot: BootData,
  paymentMethods: PaymentMethod[]
): CheckoutData => ({
  showSaveMyCard: false,
  orderId: summary.paymentIntent?.id ?? '',
  offer: buildSubscriptionOffer(summary),
  offers: [],
  items: [],
  customer: summary.customer,
  pricing: summary.pricing,
  paymentMethods,
  pks: summary.pks,
  savedCards: [],
  checkoutConfigurations: buildCheckoutConfigurations(boot),
  redirectUrl: summary.redirectUrl,
});

/**
 * Minimal boot for subscription sessions opened WITHOUT a `#boot=` hash
 * (v2-generated links). Only theme/flags/publisher identity degrade — the
 * flow itself never depends on boot.
 */
export const createSubscriptionFallbackBoot = (): BootData => ({
  checkoutTheme: {
    _id: '',
    publisherId: '',
    publisherName: '',
    buttonColor: '',
    buttonTextColor: '',
    logo: '',
    primaryColor: '',
    textColor: '',
    backgroundImage: '',
    mobileBgImage: '',
    updatedAt: '',
  } as CheckoutTheme,
  supportEmail: '',
  featureFlags: {} as Record<FeatureFlag, boolean>,
  couponsConfiguration: { publisherId: '', enabled: false },
  publisherData: { companyName: '', companyId: '', storeTabName: '', id: '' },
  companyName: '',
  storeTabName: '',
});

const noop = () => {};

export interface SubscriptionContextDeps {
  summary: SubscriptionsSummary;
  boot: BootData;
  sessionId: string;
  customer: Customer;
  pricing: Pricing;
  subscription: SubscriptionCheckoutState;
  paymentMethodSelected: PaymentMethodEnum;
  /** The APM picked inside the "Other" tile, once summary APMs overflow the box row. */
  otherMethodSelected?: ExcludeOtherPaymentMethod;
  addressDetails?: AddressDetails;
  successScreen: SuccessScreenContext;
  error: string | null;
  payment: {
    isInPaymentProcess: boolean;
    isExpressPaymentInProcess: boolean;
    isFormValid: boolean;
  };
  card: Pick<CardContext, 'cardElementRef' | 'setCardElementRef' | 'adyenCardDetailsRef'>;
  viewer: ViewerContext;
  drawer: DrawerContext;
  actions: Pick<
    Actions,
    | 'sendPaymentToServer'
    | 'setPaymentMethodSelected'
    | 'setCustomerEmail'
    | 'setIsFormValid'
    | 'setIsInPaymentProcess'
    | 'setIsExpressPaymentInProcess'
    | 'updatePriceBreakdown'
    | 'updateAddressDetails'
    | 'setError'
    | 'setSuccessScreen'
    | 'closeCheckout'
    | 'updateOtherMethodSelected'
  > & {
    runPayment: <TResponse = unknown>(
      params?: unknown,
      payFromExpressCheckout?: PayFromExpressCheckout,
      provider?: ProviderEnum
    ) => Promise<TResponse | undefined>;
  };
}

export const buildSubscriptionCheckoutContext = (deps: SubscriptionContextDeps): ICheckoutContext => {
  const paymentMethods = buildSubscriptionPaymentMethods(deps.summary);

  return {
    subscription: deps.subscription,
    sessionId: deps.sessionId,
    boot: deps.boot,
    publisherId: deps.boot.publisherData.id,
    template: getTemplateConfig(TemplateType.DEFAULT),
    customer: deps.customer,
    pricing: deps.pricing,
    totalPrice: formatTotalPrice(deps.pricing),
    offer: buildSubscriptionOffer(deps.summary),
    paymentMethodsSettings: paymentMethods,
    paymentMethodsAvailable: buildAvailablePaymentMethods(paymentMethods),
    paymentMethodSelected: deps.paymentMethodSelected,
    paymentKeys: deps.summary.pks,
    checkoutConfiguration: buildCheckoutConfigurations(deps.boot),
    featureFlags: deps.boot.featureFlags,
    // Subscriptions never take the wallet-derived ZIP/tax path: the summary is
    // fetched with the buyer's geo tax already resolved, and the only mid-session
    // recalc is the explicit ZIP form.
    sessionStartedWithoutValidTax: false,
    publisher: deps.boot.publisherData,
    redirectLink: sanitizeMerchantRedirectUrl(deps.summary.redirectUrl) ?? '',
    addressDetails: deps.addressDetails,
    successScreen: deps.successScreen,
    error: deps.error,
    isLoading: false,
    showMaintenance: false,
    orderId: deps.subscription.paymentIntent?.id,
    viewer: deps.viewer,
    drawer: deps.drawer,
    payment: { ...deps.payment, isApplePayTabOpen: false },

    actions: {
      ...deps.actions,
      // ---- inert one-time-payment actions (see module doc) ----
      startOrderPolling: noop,
      updatePromoCodeBreakdown: noop,
      setOrderId: noop,
      setSelectedCard: noop,
      setTemplate: noop,
      removeCard: noop,
      showMaintenance: noop,
      setRedirectLink: noop,
      setRedirectPayment: noop,
      openRedirectTab: noop,
      closeRedirectTab: noop,
      setThreeDSSecurityState: noop,
      setApplePayTabOpen: noop,
      resumeApplePayTabPayment: noop,
      createSession: () => Promise.reject(new Error('createSession is not supported for subscriptions')),
    },

    card: {
      // Chargebee tokenizes inside its own iframes; nothing is vaulted FE-side.
      savedCards: [],
      savedPaypalAccount: undefined,
      clearSavedPaypalAccount: noop,
      selectedCard: undefined,
      selectCard: noop,
      saveNewCard: noop,
      initialCardPayload: { cardholderName: '', zipCode: '', isSaveCard: false },
      setInitialCardPayload: noop,
      cardElementRef: deps.card.cardElementRef,
      setCardElementRef: deps.card.setCardElementRef,
      adyenCardDetailsRef: deps.card.adyenCardDetailsRef,
    },

    // ---- inert one-time-payment state (see module doc) ----
    promoCodeBreakdown: initialPromoCodeState(),
    offers: [],
    purchasedItems: [],
    otherMethodSelected: deps.otherMethodSelected,
    stripe: null,
    redirectPayment: {},
    threeDSSecurityState: { show3DSForm: false },
    paymentAttempt: undefined,
  };
};
